import type { AdminPayoutDetailDto } from '@carcheck/shared';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { PayoutStatusBadge } from './PayoutStatusBadge';
import { tokens } from '../../../design';
import { shortId } from '../_lib/displayName';

/**
 * Payout detail-drawer header (AUT-128): rounded card-icon avatar +
 * "Выплата № N" title + status badge + truncated id + close button.
 * Mirrors `OrderDetailHeader` / `ReportDetailHeader` so the surfaces
 * share visual shape. The avatar tints to the status colour family
 * (danger for failed, per the mockup) for at-a-glance triage.
 */
export const PayoutDetailHeader = ({
  detail,
  onClose,
}: {
  detail: AdminPayoutDetailDto;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const failed = detail.status === 'failed';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        p: '18px 24px',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          width: 52,
          height: 52,
          bgcolor: failed ? 'error.light' : 'info.light',
          color: failed ? 'error.dark' : 'info.dark',
        }}
      >
        <CreditCardIcon />
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }} noWrap>
          {t('payouts.detail.title', { number: `№ ${detail.payoutNumber}` })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
          <PayoutStatusBadge status={detail.status} />
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: tokens.text.tertiary }}>
            {shortId(detail.id)}
          </Typography>
        </Box>
      </Box>
      <IconButton onClick={onClose} sx={{ ml: 'auto' }} aria-label={t('payouts.detail.close')}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
